import { createContext, useContext, useState, useEffect } from 'react';
import type { ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import * as Sentry from '@sentry/react';
import { LoginPage } from '../pages/LoginPage';

// Requirements: 2.1–2.7
interface ValidationError {
  field: 'email' | 'password';
  message: string;
}

interface ValidationResult {
  valid: boolean;
  errors: ValidationError[];
}

// Requirements: 3.5
interface UserSessionContextValue {
  userEmail: string | null;
  login: (email: string) => void;
  logout: () => void;
}

const SESSION_KEY = 'stationExplorer_userEmail';

// Requirements: 2.1–2.7
// ponytail: 15 lines of validation don't need a service class
export const validateLogin = (email: string, password: string): ValidationResult => {
  const errors: ValidationError[] = [];
  const trimmed = email.trim();

  // (1) Empty/whitespace check on email
  if (!trimmed) {
    errors.push({ field: 'email', message: 'Email is required' });
  } else {
    // (2) Domain format check: exactly one @, at least 1 char before @, ends with @dtn.com, no subdomains
    const atCount = (trimmed.match(/@/g) || []).length;
    const atIndex = trimmed.indexOf('@');
    const domain = trimmed.slice(atIndex + 1).toLowerCase();

    if (atCount !== 1 || atIndex < 1 || domain !== 'dtn.com') {
      errors.push({ field: 'email', message: 'Only @dtn.com email addresses are permitted' });
    }
  }

  // (3) Password match — always check regardless of email errors (display ALL failing errors)
  const currentYear = new Date().getUTCFullYear();
  const expectedPassword = `OneObs${currentYear}!`;
  if (password !== expectedPassword) {
    errors.push({ field: 'password', message: 'Password is incorrect' });
  }

  return { valid: errors.length === 0, errors };
};

const UserSessionContext = createContext<UserSessionContextValue | undefined>(undefined);

// Requirements: 3.5
export const useUserSession = (): UserSessionContextValue => {
  const context = useContext(UserSessionContext);
  if (!context) {
    throw new Error('useUserSession must be used within UserSessionProvider');
  }
  return context;
};

interface UserSessionProviderProps {
  children: ReactNode;
}

// Requirements: 3.1–3.6, 4.1
export const UserSessionProvider = ({ children }: UserSessionProviderProps) => {
  const [userEmail, setUserEmail] = useState<string | null>(null);
  const [isChecking, setIsChecking] = useState(true);
  const navigate = useNavigate();

  // Requirements: 3.2, 3.6 — Check session on mount
  useEffect(() => {
    try {
      const stored = sessionStorage.getItem(SESSION_KEY);
      if (stored && stored.trim()) {
        setUserEmail(stored);
      }
    } catch {
      // ponytail: sessionStorage unavailable (private browsing) — fall through to login
      console.warn('⚠️ [UserSession] sessionStorage unavailable');
    }
    setIsChecking(false);
  }, []);

  // Requirements: 3.1, 4.1
  const login = (email: string) => {
    const normalizedEmail = email.trim().toLowerCase();
    try {
      sessionStorage.setItem(SESSION_KEY, normalizedEmail);
    } catch {
      console.warn('⚠️ [UserSession] Failed to write sessionStorage');
    }
    setUserEmail(normalizedEmail);
    Sentry.setUser({ email: normalizedEmail });
    if (import.meta.env.DEV) console.log(`✅ [UserSession] Login: ${normalizedEmail}`);

    // Requirements: 4.1 — Fire-and-forget tracking POST
    // ponytail: inline fetch, no TrackingClient class needed
    fetch('/api/tracking/events', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify([{
        email: normalizedEmail,
        eventType: 'login',
        timestamp: new Date().toISOString(),
        payload: {
          userAgent: navigator.userAgent.slice(0, 512),
        },
      }]),
    }).catch(() => {
      // ponytail: fire-and-forget — login succeeds regardless
      console.warn('⚠️ [UserSession] Tracking POST failed (non-blocking)');
    });
  };

  // Requirements: 3.3
  const logout = () => {
    Sentry.setUser(null);
    try {
      sessionStorage.removeItem(SESSION_KEY);
      setUserEmail(null);
      navigate('/login');
      if (import.meta.env.DEV) console.log('✅ [UserSession] Logout successful');
    } catch {
      // Retry once on failure
      try {
        sessionStorage.removeItem(SESSION_KEY);
        setUserEmail(null);
        navigate('/login');
        if (import.meta.env.DEV) console.log('✅ [UserSession] Logout successful (retry)');
      } catch (retryErr) {
        // Force-clear state and redirect regardless
        setUserEmail(null);
        navigate('/login');
        console.error('❌ [UserSession] Logout storage removal failed after retry', retryErr);
      }
    }
  };

  // Requirements: 3.6 — Block all protected content until session check completes
  if (isChecking) {
    return null;
  }

  // Requirements: 3.6 — Render LoginPage when no session
  if (!userEmail) {
    return <LoginPage onLoginSuccess={login} />;
  }

  return (
    <UserSessionContext.Provider value={{ userEmail, login, logout }}>
      {children}
    </UserSessionContext.Provider>
  );
};
